/**
 * InventoryAgingCard Component
 *
 * Forecast-page collapsible card (NEW vehicle department only). Shows the
 * latest daily snapshot of new-vehicle stock aging by OEM brand for the
 * selected location: total units, units 90+ days, % 90+, average age, and
 * the single oldest unit per brand (year/make/model + stock #).
 *
 * Same card chrome as SalesDepartmentAnalysis (white card, chevron header,
 * parent-owned `collapsed` + `onToggleCollapse`).
 */

import React, { useMemo } from 'react';
import { ChevronDownIcon, CubeIcon } from '@heroicons/react/24/outline';
import { InventoryAgingResponse } from '../../services/api';

interface InventoryAgingCardProps {
  data: InventoryAgingResponse | null;
  loading: boolean;
  error: string | null;
  locationName: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

function formatPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

function formatDate(yyyymmdd?: string): string {
  if (!yyyymmdd) return '';
  // YYYY-MM-DD → "May 8, 2026"
  const [y, m, d] = yyyymmdd.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return yyyymmdd;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ymmDescription(year: number | null, make: string | null, model: string | null): string {
  return [year || null, make || null, model || null].filter(Boolean).join(' ') || '—';
}

export const InventoryAgingCard: React.FC<InventoryAgingCardProps> = ({
  data,
  loading,
  error,
  locationName,
  collapsed = false,
  onToggleCollapse,
}) => {
  // Sort brand rows by total units desc (matches user's PowerShell ORDER BY).
  const sortedBrands = useMemo(() => {
    if (!data?.brands) return [] as Array<{ brand: string; agg: NonNullable<InventoryAgingResponse['brands'][string]> }>;
    return Object.entries(data.brands)
      .map(([brand, agg]) => ({ brand, agg }))
      .sort((a, b) => b.agg.totalUnits - a.agg.totalUnits);
  }, [data]);

  const total = data?.totalNewUnits ?? 0;
  const total90Plus = data?.totalUnits90Plus ?? 0;
  const totalPct = total > 0 ? (100 * total90Plus) / total : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <CubeIcon className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Inventory</h3>
            <p className="text-sm text-gray-500">
              {loading
                ? 'Loading…'
                : data && data.available
                  ? (
                    <>
                      {formatNumber(total)} new units in stock
                      {total > 0 && (
                        <>
                          {' '}· {formatNumber(total90Plus)} ({formatPct(totalPct)}) over 90 days
                        </>
                      )}
                      {data.snapshotDate && (
                        <span className="ml-2 text-xs text-gray-400">· as of {formatDate(data.snapshotDate)}</span>
                      )}
                    </>
                  )
                  : 'No inventory snapshot available yet'}
            </p>
          </div>
          {onToggleCollapse && (
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
            />
          )}
        </div>
      </div>

      {/* Content */}
      {!collapsed && (
        <div className="p-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">Loading inventory aging data…</div>
          ) : !data || !data.available || total === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              No inventory snapshot is available for {locationName} yet. Daily snapshots are written each
              morning at ~5 AM EST and a record will appear here on the next run.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Brand</th>
                      <th className="px-3 py-2 text-right">Total Units</th>
                      <th className="px-3 py-2 text-right">90+ Days</th>
                      <th className="px-3 py-2 text-right">% 90+</th>
                      <th className="px-3 py-2 text-right">Avg Days</th>
                      <th className="px-3 py-2 text-right">Oldest (Days)</th>
                      <th className="px-3 py-2 text-left">Oldest Unit</th>
                      <th className="px-3 py-2 text-left">Stock #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBrands.map(({ brand, agg }) => {
                      const oldest = agg.oldestUnit;
                      return (
                        <tr key={brand} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium text-gray-900">{brand}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                            {formatNumber(agg.totalUnits)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                            {formatNumber(agg.units90Plus)}
                          </td>
                          <td
                            className={`px-3 py-2 text-right tabular-nums font-medium ${agg.pct90Plus >= 30 ? 'text-red-600' : agg.pct90Plus >= 15 ? 'text-amber-600' : 'text-gray-900'}`}
                          >
                            {formatPct(agg.pct90Plus)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                            {agg.avgAgeDays.toFixed(1)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                            {oldest ? formatNumber(oldest.ageDays) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {oldest ? ymmDescription(oldest.year, oldest.make, oldest.model) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700 font-mono text-xs">
                            {oldest?.stockNumber || <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    <tr className="border-t-2 border-gray-300 bg-gray-50/60">
                      <td className="px-3 py-2 font-bold text-gray-900">Total</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-gray-900">
                        {formatNumber(total)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-gray-900">
                        {formatNumber(total90Plus)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-gray-900">
                        {formatPct(totalPct)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-400">—</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-400">—</td>
                      <td className="px-3 py-2 text-gray-400">—</td>
                      <td className="px-3 py-2 text-gray-400">—</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {data.unitsWithBadReceivedDate > 0 && (
                <div className="mt-3 text-xs text-amber-600">
                  Note: {formatNumber(data.unitsWithBadReceivedDate)} unit
                  {data.unitsWithBadReceivedDate === 1 ? ' has' : 's have'} a `datereceived` earlier than its
                  record creation date — age is measured from the later of the two to avoid inflated ages.
                </div>
              )}

              <div className="mt-3 text-xs text-gray-500">
                Source: DealerBuilt `lystock` (new, available, not deleted). Snapshot taken{' '}
                {formatDate(data.snapshotDate)}. Daily snapshots are saved for trend reporting.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
